import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  Chip,
  IconButton,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  CreditCard as CreditCardIcon,
} from '@mui/icons-material';
import Layout from '../../components/Layout';
import api from '../../services/api';

const PaymentMethods = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    card_number: '',
    cvv: '',
    cardholder_name: '',
    expiry_month: '',
    expiry_year: '',
    is_default: false,
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      const response = await api.get('/payments/methods/');
      // Ensure response.data is an array
      // Handle both direct array responses and paginated responses
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        // Paginated response
        data = response.data.results;
      } else if (response.data && typeof response.data === 'object') {
        // Single object or other structure - wrap in array or use empty
        console.warn('Unexpected payment methods response structure:', response.data);
        data = [];
      }
      setPaymentMethods(data);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      // Set empty array on error to prevent map errors
      setPaymentMethods([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setFormData({
      card_number: '',
      cvv: '',
      cardholder_name: '',
      expiry_month: '',
      expiry_year: '',
      is_default: false,
    });
    setMessage('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setMessage('');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const formatCardNumber = (value) => {
    // Remove all non-digits
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    // Add spaces every 4 digits
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    setFormData({
      ...formData,
      card_number: formatted,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const submitData = {
        ...formData,
        card_number: formData.card_number.replace(/\s/g, ''), // Remove spaces
        expiry_month: parseInt(formData.expiry_month),
        expiry_year: parseInt(formData.expiry_year),
      };

      await api.post('/payments/methods/', submitData);
      setMessage('Payment method added successfully!');
      fetchPaymentMethods();
      setTimeout(() => {
        handleCloseDialog();
        setMessage('');
      }, 1500);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      'Error adding payment method. Please check your input.';
      setMessage(errorMsg);
      console.error('Error adding payment method:', error.response?.data || error);
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (methodId) => {
    try {
      await api.post(`/payments/methods/${methodId}/set_default/`);
      fetchPaymentMethods();
    } catch (error) {
      console.error('Error setting default payment method:', error);
      alert('Error setting default payment method');
    }
  };

  const handleRemove = async (methodId) => {
    if (!window.confirm('Are you sure you want to remove this payment method?')) {
      return;
    }

    try {
      await api.post(`/payments/methods/${methodId}/remove/`);
      fetchPaymentMethods();
    } catch (error) {
      console.error('Error removing payment method:', error);
      alert('Error removing payment method');
    }
  };

  const getCardTypeIcon = (cardType) => {
    return <CreditCardIcon />;
  };

  const getCardTypeColor = (cardType) => {
    const colors = {
      visa: '#1434CB',
      mastercard: '#EB001B',
      amex: '#006FCF',
      discover: '#FF6000',
    };
    return colors[cardType] || '#666';
  };

  if (loading) {
    return (
      <Layout>
        <Container>Loading...</Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Payment Methods</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            Add Payment Method
          </Button>
        </Box>

        {!Array.isArray(paymentMethods) || paymentMethods.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              No payment methods added yet
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Add a credit or debit card to make payments faster
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
            >
              Add Your First Payment Method
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {paymentMethods.map((method) => (
              <Grid item xs={12} sm={6} md={4} key={method.id}>
                <Card
                  sx={{
                    border: method.is_default ? 2 : 1,
                    borderColor: method.is_default ? 'primary.main' : 'divider',
                  }}
                >
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getCardTypeIcon(method.card_type)}
                        <Typography variant="h6" textTransform="capitalize">
                          {method.card_type}
                        </Typography>
                      </Box>
                      {method.is_default && (
                        <Chip label="Default" color="primary" size="small" />
                      )}
                      {method.is_expired && (
                        <Chip label="Expired" color="error" size="small" />
                      )}
                    </Box>
                    <Typography variant="h5" gutterBottom>
                      {method.masked_card}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {method.cardholder_name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Expires: {String(method.expiry_month).padStart(2, '0')}/{method.expiry_year}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    {!method.is_default && (
                      <Button
                        size="small"
                        startIcon={<StarBorderIcon />}
                        onClick={() => handleSetDefault(method.id)}
                      >
                        Set Default
                      </Button>
                    )}
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleRemove(method.id)}
                    >
                      Remove
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Add Payment Method Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
          sx={{
            '& .MuiDialog-paper': {
              m: { xs: 0, sm: 2 },
            },
          }}
        >
          <DialogTitle>Add Payment Method</DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              {message && (
                <Alert 
                  severity={message.includes('success') ? 'success' : 'error'} 
                  sx={{ mb: 2 }}
                >
                  {message}
                </Alert>
              )}
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Card Number"
                    name="card_number"
                    value={formData.card_number}
                    onChange={handleCardNumberChange}
                    placeholder="1234 5678 9012 3456"
                    required
                    inputProps={{ maxLength: 19 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Cardholder Name"
                    name="cardholder_name"
                    value={formData.cardholder_name}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    select
                    SelectProps={{ native: true }}
                    label="Expiry Month"
                    name="expiry_month"
                    value={formData.expiry_month}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Month</option>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                      <option key={month} value={month}>
                        {String(month).padStart(2, '0')}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                    <TextField
                      fullWidth
                      select
                      SelectProps={{ native: true }}
                      label="Expiry Year"
                      name="expiry_year"
                      value={formData.expiry_year}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Year</option>
                      {Array.from({ length: 21 }, (_, i) => {
                        const year = new Date().getFullYear() + i;
                        return (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        );
                      })}
                    </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="CVV"
                    name="cvv"
                    type="password"
                    value={formData.cvv}
                    onChange={handleChange}
                    required
                    inputProps={{ maxLength: 4, minLength: 3 }}
                    helperText="3 or 4 digits on the back of your card"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center">
                    <input
                      type="checkbox"
                      id="is_default"
                      name="is_default"
                      checked={formData.is_default}
                      onChange={handleChange}
                    />
                    <label htmlFor="is_default" style={{ marginLeft: 8 }}>
                      Set as default payment method
                    </label>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={saving}>
                {saving ? 'Adding...' : 'Add Payment Method'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default PaymentMethods;

